import { ReactComponent as SearchIcon } from '../../../../../../assets/vectors/search.svg';
import { useMutation, useQueryClient } from 'react-query';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import Pagination from '../../../../../commons/Pagination/Pagination';
import Button from '../../../../../designSystem/Button/Button';
import { useParams } from 'react-router-dom';
import Loading from '../../../../../commons/Loading/Loading';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import { SearchCompaniesQueryResponse } from '../../../../../../types/api/endpoints/companies/searchCompany.types';
import { SEARCH_COMPANIES_QUERY_KEY, searchCompaniesQuery } from './DashboardCompaniesTable.query';
import { SearchCompaniesQuery } from './DashboardCompaniesTable.types';
import { DashboardCompaniesContext } from '../DashboardCompanies.context';
import { useConfirmedCompanies } from '../../../../../../hooks/fetchers/useConfirmedCompanies/useConfirmedCompanies';
import { formatPhoneNumber } from '../../../../../../utils/helpers/numbers/formatPhoneNumber';

export default () => {
  const { consultationId } = useParams();

  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState<string>();
  const [searchResults, setSearchResults] = useState<SearchCompaniesQueryResponse>();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(25);

  const { filters, setFiltersModalOpen } = useContext(DashboardCompaniesContext);

  const {
    data: companies,
    refetch: refetchCompanies,
    isLoading: isAllCompaniesLoading,
    isRefetching: isAllCompaniesRefetching,
  } = useConfirmedCompanies({
    page: currentPage,
    perPage: itemsPerPage,
    fieldsOfActivity: filters.fieldsOfActivity.toString(),
    region: filters.region,
    department: filters.department,
  });

  const { mutate: searchMutate, isLoading: isSearchLoading } = useMutation<
    Awaited<SearchCompaniesQueryResponse>,
    Error,
    SearchCompaniesQuery
  >((data) => searchCompaniesQuery(data), {
    mutationKey: SEARCH_COMPANIES_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: (response) => {
      setSearchResults(response);
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  useEffect(() => {
    if (searchResults) {
      searchMutate({
        search: searchValue || '',
        consultationId: parseInt(consultationId as string),
        params: {
          page: currentPage,
          perPage: itemsPerPage,
          domains: filters.domains.toString(),
          fieldsOfActivity: filters.fieldsOfActivity.toString(),
          region: filters.region,
          department: filters.department,
        },
      });
    } else {
      queryClient.cancelMutations();
      refetchCompanies();
    }
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    if (searchResults) {
      searchMutate({
        search: searchValue || '',
        consultationId: parseInt(consultationId as string),
        params: {
          page: currentPage,
          perPage: itemsPerPage,
          domains: filters.domains.toString(),
          fieldsOfActivity: filters.fieldsOfActivity.toString(),
          region: filters.region,
          department: filters.department,
        },
      });
    } else {
      queryClient.cancelMutations();
      refetchCompanies();
    }
  }, [filters.fieldsOfActivity]);

  const numberOfPages = useMemo(() => {
    if (searchResults) {
      // @ts-ignore
      return parseInt(searchResults.total / itemsPerPage) + 1;
    } else {
      // @ts-ignore
      return parseInt(companies?.total / itemsPerPage) + 1;
    }
  }, [companies, searchResults, itemsPerPage]);

  const onChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      setCurrentPage(1);
      if (value.length === 0) {
        setSearchResults(undefined);
      } else {
        searchMutate({
          search: value,
          consultationId: parseInt(consultationId as string),
          params: {
            page: currentPage,
            perPage: itemsPerPage,
            fieldsOfActivity: filters.fieldsOfActivity.toString(),
            region: filters.region,
            department: filters.department,
          },
        });
      }
    },
    [searchMutate, currentPage, itemsPerPage, filters]
  );

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [isAllCompaniesLoading, isSearchLoading, isAllCompaniesRefetching],
  });

  return (
    <>
      <div className="dashboard-commerciaux">
        <div className="dashboard-commerciaux__header">
          <SearchInput
            placeholder="Chercher une entreprise..."
            startIcon={SearchIcon}
            onChange={onChangeSearchText}
            handleSelectedSuggestion={(suggestion) => console.log(suggestion)}
          />
          <Button onClick={() => setFiltersModalOpen(true)}>
            {filters.fieldsOfActivity.length > 0
              ? `Filtrer (${filters.fieldsOfActivity.length})`
              : 'Filtrer'}
          </Button>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            {searchResults && (
              <table className="dashboard__table dashboard-commerciaux__table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Adresse</th>
                    <th>Adresse email</th>
                    <th>N° de téléphone</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults?.results?.map(
                    ({ name, address, postalCode, city, email_owner, phone_owner }, index) => {
                      return (
                        <tr key={index}>
                          <td>{name}</td>
                          <td>
                            {address}, {postalCode}, {city}
                          </td>
                          <td>{email_owner}</td>
                          <td>{phone_owner}</td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            )}
            {!searchResults && companies && companies?.results.length > 0 && (
              <table className="dashboard__table dashboard-commerciaux__table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Adresse</th>
                    <th>Adresse email</th>
                    <th>N° de téléphone</th>
                  </tr>
                </thead>
                <tbody>
                  {companies?.results.map(
                    ({ name, address, postalCode, city, email_owner, phone_owner }, index) => {
                      return (
                        <tr key={index}>
                          <td>{name}</td>
                          <td>
                            {address}, {postalCode}, {city}
                          </td>
                          <td>{email_owner}</td>
                          <td>{formatPhoneNumber(phone_owner || '')}</td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      {!isLoading && <Pagination {...{ currentPage, setCurrentPage, numberOfPages }} />}
    </>
  );
};

{
  /* <tr>
  <th>Nom</th>
  <th>Adresse</th>
  <th>Adresse email</th>
  <th>N° de téléphone</th>
  // <th>Créé le</th>
</tr> */
}
